<template>
  <view-wrapper>
    <div class="flex-grow w-full px-6 pt-10 md:px-32">

      <sub-navbar
        @navigate="$router.push({ path: `/admin/${$event}` })"
        action="edit"
      />

      <text-input
        :isPassword="true"
        :value.sync="token"
        label="Security Token"
        id="token"
      />

      <hr class="mb-5">
      <div v-if="!loading">
        <edit-item-picker
          v-if="!itemToEdit"
          @item-to-edit="loadEditItem($event.id)"
          :items="items"
          :categories="categories"
          :subCategories="subCategories"
        />

        <div v-if="itemToEdit">
          <div class="w-full">
          <!-- Name -->
          <text-input
            v-if="itemToEdit.name != undefined"
            label="Name"
            id="name"
            class="w-full sm:w-2/3"
            :error="itemToEdit.name.length === 0"
            :value.sync="itemToEdit.name"
          />
          <div class="flex flex-row w-full">
            <!-- Category -->
            <dropdown-input
              :value.sync="itemToEdit.category"
              :options="categories"
              :error="false"
              class="w-1/2 pr-5 sm:w-1/3"
              label="Category"
              id="category"
            />
            <!-- Subcategory -->
            <dropdown-input
              :value.sync="itemToEdit.subCategory"
              :options="subCategoryOptions"
              :error="false"
              class="w-1/2 sm:w-1/3"
              label="Subcategory"
              id="subcategory"
            />
          </div>
          <!-- Description -->
          <div class="w-full py-2">
            <label class="block pr-4 text-lg font-medium" for="description">Description</label>
            <textarea
              v-model="itemToEdit.description"
              :class="{ 'border-red-500': itemToEdit.description.length === 0 }"
              class="w-full p-2 border rounded outline-none focus:outline-none"
              id="description"
              type="text"
              rows="7"
            />
          </div>
          <!-- Image list -->
          <item-images
            :images.sync="itemToEdit.images"
            :disabled="disabled"
            :filesIsValid="itemToEdit.images.length > 0"
          />
        </div>

          <div class="flex">
            <general-button
              @button-click="edit"
              :disabled="loading"
              text="Update Item"
              color="green"
            />
            <general-button
              @button-click="itemToEdit = null"
              :disabled="loading"
              text="Cancel"
              color="red"
            />
          </div>
        </div>
      </div>

      <div class="flex" v-else>
        <div class="flex-shrink w-7 h-7 animate-spin text-content">
          <loading/>
        </div>
      </div>

    </div>

  </view-wrapper>
</template>

<script>
import Loading from 'vue-material-design-icons/Loading.vue';
import ViewWrapper from '../components/ViewWrapper.vue';
import SubNavbar from '../components/Admin/SubNavbar.vue';
import TextInput from '../components/Admin/TextInput.vue';
import data from '../assets/json/data.json';
import EditItemPicker from '../components/Admin/EditItemPicker.vue';
import GeneralButton from '../components/Admin/GeneralButton.vue';
import DropdownInput from '../components/Admin/DropdownInput.vue';
import ItemImages from '../components/Admin/ItemImages.vue';

// import {
//   createItems, editItems, getItem, deleteImg, uploadBase64Img, getItems, deleteItems,
// } from '../api/item';
import {
  getItems, getItem, deleteImg, uploadBase64Img,
  editItems,
} from '../api/item';

// const cloneDeep = require('lodash.clonedeep');

export default {
  name: 'Edit',
  components: {
    ViewWrapper,
    SubNavbar,
    TextInput,
    EditItemPicker,
    GeneralButton,
    DropdownInput,
    ItemImages,
    Loading,
  },
  data() {
    return {
      token: localStorage.getItem('token'), // ls = local storage
      items: [],
      categories: data.categories,
      subCategories: data.subcategories,
      itemToEdit: null,
      loading: false,
      disabled: false,
    };
  },
  computed: {
    subCategoryOptions() {
      return this.subCategories[this.itemToEdit.category].map((option) => option.name);
    },
  },
  created() {
    this.updateItems();
  },
  methods: {
    async updateItems() {
      this.items = await getItems();
      this.items.sort((a, b) => {
        if (a.name < b.name) { return -1; }
        if (a.name > b.name) { return 1; }
        return 0;
      });
    },
    loadEditItem(id) {
      this.loading = true;
      getItem(id).then((itemData) => {
        this.loading = false;
        this.itemToEdit = {
          ...itemData,
        };
      });
    },
    async edit() {
      if (!this.itemIsValid()) return;
      this.loading = true;
      const promises = [];
      const editedItem = this.wrapVerifiedItem();
      await editItems([editedItem], this.token)
        .catch(() => {
          console.log('BAD');
          throw new Error('Problem uploading the edited item. Ensure you are using the right token');
        });
      promises.push(this.saveImages());
      promises.push(this.deleteImages());
      await Promise.all(promises);
      this.updateItems();
      this.loading = false;
      this.itemToEdit = null;
    },
    async saveImages() {
      const promises = [];
      this.itemToEdit.images.forEach((imgObj) => {
        if (imgObj.new == true && imgObj.deleted == false) {
          promises.push(uploadBase64Img(
            imgObj.id,
            imgObj.extension,
            imgObj.base64,
            this.token,
          ));
        }
      });
      return Promise.all(promises)
        .catch(() => {
          throw new Error('Unable to upload images');
        });
    },
    async deleteImages() {
      const promises = [];
      this.itemToEdit.images.forEach((imgObj) => {
        if (imgObj.new == false && imgObj.deleted == true) {
          promises.push(deleteImg(
            imgObj.id,
            this.token,
          ));
        }
      });
      return Promise.all(promises)
        .catch(() => {
          throw new Error('Unable to delete images');
        });
    },
    wrapVerifiedItem() {
      const newItem = {
        id: this.itemToEdit.id,
        name: this.itemToEdit.name,
        category: this.itemToEdit.category,
        subCategory: this.itemToEdit.subCategory,
        description: this.itemToEdit.description,
        images: [],
      };
      this.itemToEdit.images.forEach((imgObj) => {
        if (imgObj.deleted == false) {
          newItem.images.push({
            id: imgObj.id,
            extension: imgObj.extension,
          });
        }
      });
      return newItem;
    },
    itemIsValid() {
      // check to see if all inputs are valid
      let valid = true;
      if (this.itemToEdit.name.length < 1) {
        this.itemToEdit.nameIsValid = false;
        valid = false;
      }
      if (this.itemToEdit.category.length < 1) {
        this.itemToEdit.categoryIsValid = false;
        valid = false;
      }
      if (this.itemToEdit.subCategory.length < 1) {
        this.itemToEdit.subcategoryIsValid = false;
        valid = false;
      }
      if (this.itemToEdit.description < 1) {
        this.itemToEdit.descriptionIsValid = false;
        valid = false;
      }
      if (this.itemToEdit.images.length == 0) {
        this.itemToEdit.filesIsValid = false;
        valid = false;
      }
      return valid;
    },
  },
};
</script>
